import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { simulateCreditCardDebt, minimumPaymentTrapWarning } from './creditCardDebtUtils';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const CreditCardDebtApp = () => {
  const clampPercent = (value) => {
    if (value === '') return '';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return value;
    return Math.min(100, Math.max(0, numeric));
  };
  const [startingBalanceGBP, setStartingBalanceGBP] = useState(2500);
  const [aprPercent, setAprPercent] = useState(24.9);
  const [monthlyPaymentGBP, setMonthlyPaymentGBP] = useState(120);
  const [paymentStrategy, setPaymentStrategy] = useState('pay_fixed');
  const [minPaymentPercent, setMinPaymentPercent] = useState(3);
  const [promoAprPercent, setPromoAprPercent] = useState('');
  const [promoMonths, setPromoMonths] = useState('');
  const [showAllRows, setShowAllRows] = useState(false);

  const { result, error } = useMemo(() => {
    try {
      const promoApr = promoAprPercent === '' ? null : promoAprPercent;
      const promoTerm = promoMonths === '' ? null : promoMonths;
      const output = simulateCreditCardDebt({
        startingBalanceGBP,
        aprPercent,
        monthlyPaymentGBP,
        minPaymentMode: 'percent',
        minPaymentPercent,
        paymentStrategy,
        promoAprPercent: promoApr !== null && promoTerm !== null ? promoApr : undefined,
        promoMonths: promoApr !== null && promoTerm !== null ? promoTerm : undefined,
        maxMonths: 600,
        roundingMode: 'pennies_end',
        interestMethod: 'monthly_compound',
      });

      return { result: output, error: null };
    } catch (err) {
      return { result: null, error: err.message };
    }
  }, [
    startingBalanceGBP,
    aprPercent,
    monthlyPaymentGBP,
    minPaymentPercent,
    paymentStrategy,
    promoAprPercent,
    promoMonths,
  ]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);

  const warning = result ? minimumPaymentTrapWarning(result) : null;
  const affordabilityWarning = useMemo(() => {
    if (!result || result.schedule.length === 0) return null;
    const firstMonth = result.schedule[0];
    if (firstMonth.paymentMade < firstMonth.interestCharged) {
      return 'Monthly payment is below the interest charged, so the balance grows over time.';
    }
    return null;
  }, [result]);
  const scheduleRows = result
    ? (showAllRows ? result.schedule : result.schedule.slice(0, 12))
    : [];

  const monthsToClearLabel = result?.monthsToClear ?? 'Not cleared';
  const maxMonthsLabel = result?.schedule?.length ?? 0;
  const balanceLabel =
    result?.monthsToClear === null
      ? `Balance at max months (${maxMonthsLabel})`
      : 'Final balance (paid off)';
  const minimumLabel = `Minimum (${minPaymentPercent}%)`;
  const chartData = useMemo(() => {
    if (!result) return null;
    const noInterestResult = simulateCreditCardDebt({
      startingBalanceGBP,
      aprPercent: 0,
      monthlyPaymentGBP,
      minPaymentMode: 'percent',
      minPaymentPercent,
      paymentStrategy,
      maxMonths: 600,
      roundingMode: 'pennies_end',
      interestMethod: 'monthly_compound',
    });

    const buildCumulativePaid = (schedule) => {
      let runningTotal = 0;
      return schedule.map((row) => {
        runningTotal += row.paymentMade;
        return Number.isFinite(runningTotal) ? runningTotal : 0;
      });
    };

    const labels = result.schedule.map((row) => `M${row.monthIndex}`);
    const paidWithInterest = buildCumulativePaid(result.schedule);
    const paidNoInterest = buildCumulativePaid(noInterestResult.schedule);

    return {
      labels,
      datasets: [
        {
          label: 'Cumulative paid (with compounding interest)',
          data: paidWithInterest,
          borderColor: '#1976d2',
          backgroundColor: 'rgba(25, 118, 210, 0.15)',
          tension: 0.3,
          fill: true,
          pointRadius: paidWithInterest.length > 120 ? 0 : 2,
        },
        {
          label: 'Cumulative paid (0% APR baseline)',
          data: paidNoInterest,
          borderColor: '#c62828',
          backgroundColor: 'rgba(198, 40, 40, 0.08)',
          borderDash: [6, 6],
          tension: 0.3,
          fill: false,
          pointRadius: paidNoInterest.length > 120 ? 0 : 2,
        },
      ],
    };
  }, [result, startingBalanceGBP, monthlyPaymentGBP, minPaymentPercent, paymentStrategy]);

  return (
    <>
      <Helmet>
        <title>Credit Card Compound Debt Calculator | Talk About Finance</title>
        <meta
          name="description"
          content="Simulate credit card debt growth and repayment with compounding interest, minimum payments, and optional promos."
        />
        <link rel="canonical" href="https://talkabout.finance/apps/credit-card-compound-debt" />
      </Helmet>

      <Container sx={{ mt: 6, mb: 8, pb: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: (theme) => theme.shadows[1],
            background: (theme) =>
              theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #f7f9fc 0%, #ffffff 45%, #f7f9fc 100%)'
                : 'linear-gradient(135deg, #1d1f24 0%, #17181c 45%, #1d1f24 100%)',
          }}
        >
          <Stack spacing={1.5}>
            <Box>
              <Button variant="outlined" color="primary" component={RouterLink} to="/apps">
                Back to apps
              </Button>
            </Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
              Calculator
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Credit Card Compound Debt
              </Typography>
              <Chip label="Beta" color="primary" size="small" />
            </Box>
            <Typography variant="body1" color="text.secondary">
              See how compounding interest makes credit card debt linger longer than you expect, even with regular payments.
            </Typography>
          </Stack>
        </Paper>

        <Paper elevation={3} sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Inputs
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <Box sx={{ minWidth: 220 }}>
              <TextField
                label="Starting balance"
                value={startingBalanceGBP}
                onChange={(e) => setStartingBalanceGBP(e.target.value)}
                type="number"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  inputProps: { min: 0, step: 50 },
                }}
              />
            </Box>
            <Box sx={{ minWidth: 180 }}>
              <TextField
                label="APR"
                value={aprPercent}
                onChange={(e) => setAprPercent(clampPercent(e.target.value))}
                type="number"
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  inputProps: { min: 0, max: 100, step: 0.1 },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Box sx={{ minWidth: 240, flex: '1 1 240px' }}>
                <FormControl fullWidth>
                  <InputLabel id="payment-strategy-label">Payment strategy</InputLabel>
                  <Select
                    labelId="payment-strategy-label"
                    label="Payment strategy"
                    value={paymentStrategy}
                    onChange={(e) => setPaymentStrategy(e.target.value)}
                  >
                    <MenuItem value="pay_fixed">Pay fixed amount</MenuItem>
                    <MenuItem value="pay_minimum">Pay minimum only</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {paymentStrategy === 'pay_fixed' && (
                <Box sx={{ minWidth: 200, flex: '0 1 200px' }}>
                  <TextField
                    label="Monthly payment"
                    value={monthlyPaymentGBP}
                    onChange={(e) => setMonthlyPaymentGBP(e.target.value)}
                    type="number"
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">£</InputAdornment>,
                      inputProps: { min: 0, step: 10 },
                    }}
                  />
                </Box>
              )}
              {paymentStrategy === 'pay_minimum' && (
                <Box sx={{ minWidth: 160, flex: '0 1 160px' }}>
                  <TextField
                    label="Minimum % (monthly)"
                    value={minPaymentPercent}
                    onChange={(e) => setMinPaymentPercent(clampPercent(e.target.value))}
                    type="number"
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      inputProps: { min: 0, max: 100, step: 0.1 },
                    }}
                  />
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, width: '100%' }}>
              <Box sx={{ minWidth: 200 }}>
                <TextField
                  label="Promo APR (optional)"
                  value={promoAprPercent}
                  onChange={(e) => setPromoAprPercent(e.target.value)}
                  type="number"
                  fullWidth
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    inputProps: { min: 0, step: 0.1 },
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 200 }}>
                <TextField
                  label="Promo months (optional)"
                  value={promoMonths}
                  onChange={(e) => setPromoMonths(e.target.value)}
                  type="number"
                  fullWidth
                  InputProps={{
                    inputProps: { min: 0, step: 1 },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          {result && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Summary
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Months to clear</TableCell>
                      <TableCell align="right">{monthsToClearLabel}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Total interest paid</TableCell>
                      <TableCell align="right">{formatCurrency(result.totalInterestPaid)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Total paid</TableCell>
                      <TableCell align="right">{formatCurrency(result.totalPaid)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{balanceLabel}</TableCell>
                      <TableCell align="right">{formatCurrency(result.finalBalance)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
              {warning && (
                <Typography sx={{ mt: 2, color: 'warning.main' }}>
                  {warning}
                </Typography>
              )}
              {affordabilityWarning && (
                <Typography sx={{ mt: 1, color: 'warning.main' }}>
                  {affordabilityWarning}
                </Typography>
              )}
            </Box>
          )}

          {chartData && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                What you’ve paid so far
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, height: 320 }}>
                <Line
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true, position: 'bottom' },
                      tooltip: { callbacks: { label: (ctx) => `£${ctx.parsed.y.toFixed(2)}` } },
                    },
                    scales: {
                      x: { ticks: { maxTicksLimit: 12 } },
                      y: { ticks: { callback: (value) => `£${value}` } },
                    },
                  }}
                />
              </Paper>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The dashed line shows how much you would have paid at 0% APR. The widening gap highlights the cost of compounding interest.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This model does not include card fees such as late payment or cash advance charges.
              </Typography>
            </Box>
          )}

          {result && (
            <Box sx={{ mt: 4 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Schedule
                </Typography>
                <Button size="small" onClick={() => setShowAllRows((prev) => !prev)}>
                  {showAllRows ? 'Show first 12' : 'Show all'}
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Showing {scheduleRows.length} of {result.schedule.length} months.
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Start</TableCell>
                      <TableCell align="right">Purchases</TableCell>
                      <TableCell align="right">Interest</TableCell>
                      <TableCell align="right">{minimumLabel}</TableCell>
                      <TableCell align="right">Payment</TableCell>
                      <TableCell align="right">End</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scheduleRows.map((row) => (
                      <TableRow key={row.monthIndex}>
                        <TableCell>{row.monthIndex}</TableCell>
                        <TableCell align="right">{formatCurrency(row.startingBalance)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.purchasesAdded)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.interestCharged)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.minimumPayment)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.paymentMade)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.endingBalance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}
        </Paper>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            About these calculations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Interest is converted from APR into an effective monthly compound rate and applied monthly. Results are illustrative and based on the
            assumptions shown. Please verify with your card provider for exact billing rules.
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default CreditCardDebtApp;
